import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';

type GiftAnimation = {
  AnimationKey: string;
  Name: string;
  Description?: string;
  FileKey: string;
  FileUrl: string;
  IsActive: boolean;
  createdAt?: string;
};

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

const loadLottie = (): Promise<any> =>
  new Promise((resolve) => {
    const win = window as any;
    if (win.lottie) return resolve(win.lottie);
    const existing = document.querySelector('script[data-lottie]');
    if (existing) {
      const interval = setInterval(() => {
        if (win.lottie) { clearInterval(interval); resolve(win.lottie); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.setAttribute('data-lottie', '1');
    script.onload = () => resolve(win.lottie);
    document.head.appendChild(script);
  });

const LottiePreview: React.FC<{ url: string; size?: number }> = ({ url, size = 120 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [lottie, res] = await Promise.all([loadLottie(), fetch(url)]);
        if (cancelled || !containerRef.current) return;
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (cancelled || !containerRef.current) return;
        animRef.current = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: data,
        });
      } catch {
        if (!cancelled) setError(true);
      }
    };
    run();
    return () => {
      cancelled = true;
      animRef.current?.destroy();
    };
  }, [url]);

  if (error) return (
    <div className='flex items-center justify-center h-full text-xs text-gray-500'>Preview unavailable</div>
  );
  return <div ref={containerRef} style={{ width: size, height: size }} />;
};

const GiftAnimations: React.FC = () => {
  const navigate = useNavigate();
  const [animations, setAnimations] = useState<GiftAnimation[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingKey, setDeletingKey] = useState('');

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) { toast.error('No token found. Please log in.'); return null; }
    if (!API_KEY) { toast.error('Missing API key.'); return null; }
    return getApiHeaders(token);
  }, []);

  const fetchAnimations = useCallback(async () => {
    const headers = headerWithAuth();
    if (!headers) return;
    setLoading(true);
    try {
      const res = await fetch(`${walletApiBase}/getGiftAnimations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ IncludeInactive: true }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load animations');
      setAnimations(json.data?.animations || []);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [headerWithAuth]);

  useEffect(() => { fetchAnimations(); }, [fetchAnimations]);

  const handleDelete = async (animationKey: string) => {
    const headers = headerWithAuth();
    if (!headers) return;
    setDeletingKey(animationKey);
    try {
      const res = await fetch(`${walletApiBase}/deleteGiftAnimation`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ AnimationKey: animationKey }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to delete');
      toast.success('Animation deleted');
      setAnimations((prev) => prev.filter((a) => a.AnimationKey !== animationKey));
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setDeletingKey('');
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Gift Animations</h1>
            <p className='text-sm text-gray-400'>Lottie JSON animations attached to token gifts.</p>
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-sm text-gray-400'>{loading ? 'Loading...' : `${animations.length} animations`}</span>
            <button
              onClick={() => navigate('/gift-animations/new')}
              className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
            >
              + Upload Animation
            </button>
          </div>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Fetching animations...</div>
        ) : animations.length === 0 ? (
          <div className='text-sm text-gray-400'>No animations found. Upload one to get started.</div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
            {animations.map((anim) => (
              <div key={anim.AnimationKey} className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg'>
                <div className='h-48 bg-white/10 flex items-center justify-center'>
                  <LottiePreview url={anim.FileUrl} size={140} />
                </div>
                <div className='p-4 space-y-2'>
                  <div className='text-sm font-semibold text-white'>{anim.Name}</div>
                  <div className='text-xs text-gray-400'>Key: {anim.AnimationKey}</div>
                  {anim.Description && (
                    <div className='text-xs text-gray-500'>{anim.Description}</div>
                  )}
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${
                    anim.IsActive
                      ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                      : 'text-red-300 border-red-400/40 bg-red-500/10'
                  }`}>
                    {anim.IsActive ? 'Active' : 'Inactive'}
                  </span>
                  <a
                    href={anim.FileUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='block text-xs text-[#FF9C15] hover:underline break-all'
                  >
                    {anim.FileUrl}
                  </a>
                  <button
                    onClick={() => handleDelete(anim.AnimationKey)}
                    disabled={deletingKey === anim.AnimationKey}
                    className='mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                  >
                    {deletingKey === anim.AnimationKey ? 'Deleting...' : 'Delete Animation'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftAnimations;
